<template>
  <v-container tag="main">
    <section class="i-sponsors">
      <h1 style="margin-bottom: 15px">Sponsors</h1>
      <v-layout row>
        <form-modal
          v-model="showModal"
          v-if="isWriteAvailable"
          :fetchParam="fetchSponsorsListParam"
        />
        <form-tasks
          :taskFields="taskFields"
          :sponsorsTaskList="sponsorsTaskList"
          @changeTaskStatus="changeTaskStatus"
        />
        <reminder-modal
          :taskFields="RemindertaskFields"
          :sponsorsTaskList="sponsorsTaskList"
          @changeTaskStatus="changeTaskStatus"
          v-if="isWriteAvailable"
        />
        <v-btn color="primary" dark class="mb-2" @click="reset()">Reset</v-btn>
        <v-btn @click="downloadExcel('sponsors')">Download Sponsors</v-btn>
        <v-btn @click="openReminderModal()"><v-icon class="openReminderModal">drafts</v-icon>Reminders</v-btn>
      </v-layout>
      <v-layout row>
        <v-flex xs2>
          <v-select
            v-if="sponsorsTypesTitlesArr"
            :items="sponsorsTypesTitlesArr"
            v-model="search.type"
            label="Types"/>
        </v-flex>
        <v-flex xs2 offset-xs1>
          <v-select
            v-if="sponsorsTiersTitlesArr"
            :items="sponsorsTiersTitlesArr"
            v-model="search.tier"
            label="Tiers"/>
        </v-flex>

        <v-flex xs-3 offset-xs1>
          <v-text-field
            append-icon="search"
            label="Company"
            single-line
            hide-details
            v-model="search.company"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-data-table
        v-if="sponsorsList"
        :headers="headers"
        class="elevation-1 wrap"
        :pagination.sync="pagination"
        item-key="id"
        :items="fullSponsorsList"
        :total-items="totalItemsInTable"
        :disable-initial-sort="true"
      >
        <!--:loading="loading" -->
        <template slot="items" slot-scope="props">
          <td class="py-4">
            <v-avatar><img :src="(props.item.logoUrl) || 'http://via.placeholder.com/250x100.jpg'" alt="no image"></v-avatar>
          </td>
          <td class="py-4">{{props.item.company}}</td>
          <td class="py-4">
            <span v-for="(id) in props.item.sponsorTypesIds" :key="'types' + id">
              {{getTypeNameById(id)}}
            </span><br>
          </td>
          <td class="py-4">
            <span v-for="(id) in props.item.sponsorTiersIds" :key="'tires' + id">
              {{getTiersNameById(id)}}<br>
            </span>
          </td>
          <td class="py-4">{{registeredVia(props.item.mediaKitId)}}</td>
          <td class="py-4">{{props.item.siteVisitCount}}</td>
          <td class="py-4">
            <span v-if="props.item.tasks">
              {{props.item.tasks.length}}/{{sponsorsTaskList.length}}
            </span>
            <span v-else>
              0/{{sponsorsTaskList.length}}
            </span>
          </td>
          <td class="py-4">
            <v-tooltip bottom>
              <v-btn
                color="green"
                fab flat
                small
                width="30"
                height="30"
                slot="activator"
                @click.stop="openTasksModal(props.item.id, props.item.tasks)"
                :disabled="!isWriteAvailable"
              >
                <v-icon>class</v-icon>
              </v-btn>
              <span>tasks</span>
            </v-tooltip>
          </td>
          <td class="py-4">{{props.item.status}}</td>
          <td class="py-4">{{props.item.boothNumber}}</td>
          <td class="py-4">{{props.item.weight}}</td>
          <td class="text-xs-center">
            <v-tooltip bottom>
              <v-btn color="lime"
                fab flat
                slot="activator"
                @click.stop="edit(props.item)"
                :disabled="!isWriteAvailable"
              >
                <v-icon>create</v-icon>
              </v-btn>
              <span>Edit</span>
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <v-tooltip bottom>
              <v-btn
                color="black"
                fab flat
                slot="activator"
                @click.stop="publish(props.item.id)"
                :disabled="!isWriteAvailable"
              >
                <v-icon>publish</v-icon>
              </v-btn>
              <span>To publish</span>
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <v-tooltip bottom>
              <v-btn
                color="orange"
                fab flat
                slot="activator"
                @click.stop="draft(props.item.id)"
                :disabled="!isWriteAvailable"
              >
                <v-icon>description</v-icon>
              </v-btn>
              <span>To draft</span>
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <v-tooltip bottom>
              <v-btn
                color="error"
                fab flat
                slot="activator"
                @click.stop="remove(props.item.id)"
                :disabled="!isWriteAvailable"
              >
                <v-icon>delete</v-icon>
              </v-btn>
              <span>Delete</span>
            </v-tooltip>
          </td>
        </template>
        <template slot="pageText" slot-scope="{}">
          Page {{pagination.page}} of {{totalSponsorsPages}}
        </template>
      </v-data-table>
    </section>
  </v-container>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import { timeSettings } from '@/modules/settings/'
  import debounce from 'lodash.debounce'
  import { equals, path, clone } from 'ramda'
  import FormModal from './formModal'
  import FormTasks from './formTasks'
  import ReminderModal from './reminderModal'
  import mixins from '@/utils/mixins'

  export default {
    name: 'SponsorsList',
    mixins: [mixins.isWriteAvailable],
    components: {
      FormModal,
      FormTasks,
      ReminderModal
    },
    data () {
      return {
        headers: [
          { text: 'Image', search: false, sortable: false, value: 'image', width: 50 },
          { text: 'Company name', value: 'company', search: true, sortable: true, width: 200 },
          { text: 'Sponsor type', value: 'type', search: false, sortable: false, width: 100 },
          { text: 'Sponsor tier', value: 'name', search: true, sortable: false, width: 200 },
          { text: 'Registered via', value: 'mediaKitId', search: false, sortable: true, width: 100 },
          { text: 'Clicks', value: 'siteVisitCount', search: true, sortable: true, width: 200 },
          { text: 'To-Do Status', value: 'To-Do status', search: true, sortable: false, width: 130 },
          { text: 'Set progress', search: true, sortable: false, width: 130 },
          { text: 'Status', value: 'status', search: false, sortable: true, width: 100 },
          { text: 'Booth', value: 'booth', search: false, sortable: true, width: 100 },
          { text: 'Weight', value: 'weight', search: false, sortable: true, width: 30 },
          { text: 'Edit', value: 'edit', search: false, sortable: false, align: 'center', width: 50 },
          { text: 'Publish', value: 'publish', search: false, sortable: false, align: 'center', width: 50 },
          { text: 'Draft', value: 'draft', search: false, sortable: false, align: 'center', width: 50 },
          { text: 'Delete', value: 'delete', search: false, sortable: false, align: 'center', width: 50 }
        ],
        pagination:
          {
            rowsPerPage: 5
          },
        fullSponsorsList: [],
        search: {
          company: '',
          type: '',
          tier: ''
        },
        showModal: false,
        modalGallery: false,
        taskFields: {
          showTasks: false,
          id: '',
          tasks: []
        },
        RemindertaskFields: {
          showTasks: false,
          id: '',
          tasks: []
        },
        fetchSponsorsListParam: null,
        // DON'T DELETE! need for isWriteAvailable mixin
        scopeForWrite: 'sponsors:write'
      }
    },
    computed: {
      ...mapGetters([
        'sponsorsList',
        'sponsorsTypeList',
        'sponsorsTaskList',
        'sponsorsTiersList',
        'totalSponsorsPages',
        'totalSponsorsItems'
      ]),
      sponsorsTypesTitlesArr () {
        if (!this.sponsorsTypeList) return []
        let filteredArr = []
        this.sponsorsTypeList.forEach((item, i) => {
          filteredArr[i] = item.title
        })
        return filteredArr
      },
      sponsorsTiersTitlesArr () {
        if (!this.sponsorsTiersList) return []
        let filteredArr = []
        this.sponsorsTiersList.forEach((item, i) => {
          filteredArr[i] = item.title
        })
        return filteredArr
      },
      totalItemsInTable () {
        return this.totalSponsorsItems || (this.totalSponsorsPages * this.pagination.rowsPerPage)
      },
      sponsorsTypesNames () {
        const reducedObj = this.sponsorsTypeList.reduce((acc, item) => {
          acc[item.id] = item.title
          return acc
        }, {})
        return reducedObj
      },
      sponsorsTiersNames () {
        if (!this.sponsorsTiersList) return []
        const reducedObj = this.sponsorsTiersList.reduce((acc, item) => {
          acc[item.id] = item.title
          return acc
        }, {})
        return reducedObj
      }
    },
    watch: {
      sponsorsList (newVal) {
        this.fullSponsorsList = newVal
      },
      search: {
        handler (newVal) {
          if (!newVal) return
          this.getSponsors()
        },
        deep: true
      },
      pagination: {
        handler (val, oldVal) {
          if (val.totalItems !== oldVal.totalItems) return
          this.getSponsors()
        },
        deep: true
      },
    },
    methods: {
      equals,
      path,
      ...mapActions([
        'fetchSponsorsList',
        'fetchSponsorTasksList',
        'fetchSponsorTypesList',
        'fetchSponsorTiersList',
        'fetchSponsorsTagsList',
        'editSponsor',
        'updateSponsorPublish',
        'updateSponsorDraft',
        'deleteSponsor',
        'downloadExcel'
      ]),
      registeredVia (mediaKitId) {
        return mediaKitId ? 'MediaKit' : 'Admin'
      },
      edit (sponsor) {
        this.$bus.$emit('editSponsor', sponsor)
      },
      openTasksModal (id, tasks) {
        this.taskFields = {
          showTasks: true,
          id,
          tasks
        }
      },
      openReminderModal (id, tasks) {
        this.RemindertaskFields = {
          showTasks: true,
          id,
          tasks
        }
      },
      changeTaskStatus ({ id, tasks }) {
        const res = this.fullSponsorsList.map(item => {
          if (id === item.id) {
            item.tasks = tasks
            item = clone(item)
          }
          return item
        })
        this.fullSponsorsList = res
      },
      async publish (id) {
        await this.updateSponsorPublish([id])
        this.getSponsors()
      },
      async draft (id) {
        await this.updateSponsorDraft([id])
        this.getSponsors()
      },
      async remove (id) {
        await this.deleteSponsor(id)
        this.getSponsors()
      },
      getTypeNameById (id) {
        return path(['sponsorsTypesNames', id], this)
      },
      getTiersNameById (id) {
        return path(['sponsorsTiersNames', id], this)
      },
      getKeyByValue (object, value) {
        return Object.keys(object).find(key => object[key] === value);
      },
      reset () {
        this.search.type = ''
        this.search.tier = ''
        this.search.company = ''
        this.getSponsors()
      },
      getSponsors: debounce(async function () {
        const { sortBy, descending, page, rowsPerPage } = this.pagination
        let itemsOnPage = equals(rowsPerPage, -1) ? this.totalSponsorsItems : rowsPerPage
        const sort = descending ? 'Desc' : 'Asc'
        const name = sortBy ? sortBy[0].toUpperCase() + sortBy.slice(1) : null
        const newPage = page - 1
        const pageCheck = newPage > 0 ? newPage : 0
        const getParams = {
          page: pageCheck,
          itemsOnPage,
          search: {
            type: path(['type'], this.search)? this.getKeyByValue(this.sponsorsTypesNames,  path(['type'], this.search)) : '',
            tier: path(['tier'], this.search) ? this.getKeyByValue(this.sponsorsTiersNames, path(['tier'], this.search)) : '',
            company: path(['company'], this.search)
          },
          sort
        }
        try {
          await this.fetchSponsorsList(getParams)
          this.fetchSponsorsListParam = clone(getParams)
        } catch (e) {
          throw (e)
        }
      }, timeSettings.delayTime)
    },
    mounted () {
      this.fetchSponsorTasksList()
      this.fetchSponsorTypesList()
      this.fetchSponsorTiersList()
      this.fetchSponsorsTagsList()
      this.getSponsors()
    }
  }
</script>

<style>
  .openReminderModal {
    margin-right: 10px;
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/Sponsors/SponsorsList/index.vue